import type { ApiError } from '@arpm/shared';

import { freshAccessToken } from '@/auth/oidc.js';
import { useAuthStore } from '@/stores/auth.js';

/**
 * Унифицированный API-клиент events-web.
 *
 * apiRequest('/path', { method, data | body, params, headers, signal }) —
 * ручные вызовы из queries/*. Авторизация: Bearer-токен берётся из
 * useAuthStore. ApiError разворачивается единообразно — фронт всегда ловит
 * ApiClientError с правильным statusCode.
 */

const baseUrl = import.meta.env.VITE_API_URL.replace(/\/$/, '');

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly apiError: ApiError | null,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

type Primitive = string | number | boolean | null | undefined;
type ParamsRecord = Record<string, Primitive | Primitive[]>;

interface ExtendedInit extends Omit<RequestInit, 'body'> {
  /** orval отдаёт тело здесь; если задано — сериализуется в JSON body */
  data?: unknown;
  /** orval отдаёт query-параметры; собираем в querystring */
  params?: ParamsRecord;
  /** обратная совместимость с ручными вызовами */
  body?: BodyInit | null;
}

export async function apiRequest<T>(path: string, init: ExtendedInit = {}): Promise<T> {
  const authStore = useAuthStore();

  const url = appendParams(`${baseUrl}${path}`, init.params);
  const headers = new Headers(init.headers);
  if (authStore.accessToken) {
    // Токен живёт пять минут, а анкету заполняют дольше. Берём свежий: если
    // срок на исходе, он обновится молча — иначе отправка длинной формы
    // падала бы отказом авторизации ровно на последней кнопке.
    const token = (await freshAccessToken()) ?? authStore.accessToken;
    headers.set('Authorization', `Bearer ${token}`);
  }

  let body: BodyInit | null | undefined = init.body;
  if (body === undefined && init.data !== undefined) {
    if (init.data instanceof FormData) {
      body = init.data;
    } else {
      body = JSON.stringify(init.data);
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
    }
  } else if (body && !headers.has('Content-Type') && typeof body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    method: init.method,
    headers,
    body: body ?? null,
    signal: init.signal,
    credentials: 'omit',
  });

  if (!response.ok) {
    let apiError: ApiError | null = null;
    try {
      apiError = (await response.json()) as ApiError;
    } catch {
      /* body не json */
    }
    throw new ApiClientError(
      apiError?.message ?? `${response.status} ${response.statusText}`,
      response.status,
      apiError,
    );
  }

  // Пустое тело — это не ошибка разбора.
  //
  // Ручка, возвращающая `void`, у Nest отвечает 200 с пустым телом, а не 204:
  // явный `@HttpCode` стоит далеко не везде (в events-api — ни у одной из
  // двадцати одной такой ручки). `response.json()` на пустой строке бросает
  // SyntaxError, мутация уходит в ошибку, `onSuccess` не срабатывает — и кэш
  // не обновляется, хотя на сервере всё сохранено. Снаружи это выглядит как
  // «изменение видно только после перезагрузки страницы».
  if (response.status === 204 || response.status === 205) {
    return undefined as T;
  }
  const text = await response.text();
  if (text === '') {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

/** Удобные шорткаты для ручных вызовов. */
export const apiClient = {
  get: <T>(path: string) => apiRequest<T>(path, { method: 'GET' }),
  post: <T>(path: string, data?: unknown) => apiRequest<T>(path, { method: 'POST', data }),
  put: <T>(path: string, data?: unknown) => apiRequest<T>(path, { method: 'PUT', data }),
  patch: <T>(path: string, data?: unknown) => apiRequest<T>(path, { method: 'PATCH', data }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

/** Скачать бинарный ответ (например, Excel-экспорт) с авторизацией. */
export async function apiBlob(path: string): Promise<Blob> {
  const authStore = useAuthStore();
  const headers = new Headers();
  if (authStore.accessToken) {
    const token = (await freshAccessToken()) ?? authStore.accessToken;
    headers.set('Authorization', `Bearer ${token}`);
  }
  const response = await fetch(`${baseUrl}${path}`, { headers, credentials: 'omit' });
  if (!response.ok) {
    throw new ApiClientError(`${response.status} ${response.statusText}`, response.status, null);
  }
  return response.blob();
}

function appendParams(url: string, params: ParamsRecord | undefined): string {
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const v of value) if (v != null) search.append(key, String(v));
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  if (!qs) return url;
  return url + (url.includes('?') ? '&' : '?') + qs;
}
